/**
 * ForcePasswordChange — shown when an account was created with an admin-set
 * default password (password_change_required). The user must set a new password
 * before they can use the portal. On success the flag is cleared server-side and
 * we refresh the session, which lets ProtectedRoute through to the app.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, ShieldCheck } from 'lucide-react';
import api from '../lib/api/client';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

export default function ForcePasswordChange() {
  const navigate = useNavigate();
  const { user, checkAuthStatus, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 12) {
      toast.error('New password must be at least 12 characters');
      return;
    }
    if (newPassword === currentPassword) {
      toast.error('Choose a password different from the temporary one');
      return;
    }
    setSaving(true);
    try {
      await api.post('/auth/change-password/', {
        old_password: currentPassword,
        new_password: newPassword,
        new_password_confirm: confirmPassword,
      });
      toast.success('Password updated — welcome aboard');
      await checkAuthStatus();   // refreshes user; password_change_required now false
      navigate('/', { replace: true });
    } catch (err: unknown) {
      const fields = (err as { response?: { data?: Record<string, string[]> } }).response?.data;
      if (fields?.['old_password']) {
        toast.error(fields['old_password'][0] || 'Current password is incorrect');
      } else if (fields?.['new_password']) {
        toast.error(fields['new_password'][0] || 'Invalid new password');
      } else {
        toast.error('Could not update password');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <CardTitle>Set your password</CardTitle>
          </div>
          <CardDescription>
            Your account was created with a temporary password{user?.email ? ` for ${user.email}` : ''}.
            Please choose a new password to continue.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="current">Temporary password</Label>
              <Input id="current" type="password" value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)} autoComplete="current-password" required />
            </div>
            <div>
              <Label htmlFor="new">New password</Label>
              <Input id="new" type="password" value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password" required />
            </div>
            <div>
              <Label htmlFor="confirm">Confirm new password</Label>
              <Input id="confirm" type="password" value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password" required />
            </div>
            <Button type="submit" className="w-full" disabled={saving}>
              {saving ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</>) : 'Set password & continue'}
            </Button>
            <button type="button" onClick={() => { logout(); navigate('/login', { replace: true }); }}
              className="w-full text-xs text-muted-foreground hover:underline">
              Sign out
            </button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
